/**
 * The Journal's physical layout: which directories exist, where a record file
 * lives, and which paths any transaction is allowed to touch.
 *
 * Every path in this package is relative to the Journal root, which is why
 * nothing here is absolute — the approved-root checks below are string prefix
 * comparisons, and an absolute path would turn each of them into a
 * substrate-specific parse that a provider-supplied string could walk out of.
 *
 * The two "approved" sets are separate on purpose and must stay separate:
 *
 *  - {@link JOURNAL_RECOVERY_ROOTS} bounds what may be *read out* and handed to
 *    the export port (F-JP-33). It includes the quarantine, because a preserved
 *    record is the whole point of exporting one.
 *  - {@link JOURNAL_DELETION_ROOTS} bounds what a deletion transaction may
 *    *move* (F-JP-21). It excludes the quarantine and staging, because a
 *    transaction that could stage a quarantined file could destroy the only
 *    intact copy of a record the user has not recovered yet.
 */

import type { JournalRecordType } from "@ff/domain";

import { journalPathJoin } from "./files";

/* ------------------------------------------------------------------------- *
 * Directories
 * ------------------------------------------------------------------------- */

/**
 * The Journal root. It is the empty string because every path this package
 * produces is already relative to it; naming it keeps the ten-directory list
 * below honest rather than making the root an unlisted special case.
 */
export const JOURNAL_ROOT = "";

export const JOURNAL_ENTRIES_DIRECTORY = "Entries";
export const JOURNAL_DRAFTS_DIRECTORY = "Drafts";
export const JOURNAL_GOALS_DIRECTORY = "Goals";
export const JOURNAL_ATTACHMENTS_DIRECTORY = "Attachments";
export const JOURNAL_STAGING_DIRECTORY = "Staging";
export const JOURNAL_QUARANTINE_DIRECTORY = "Quarantine";

/**
 * All ten directories F-JP-18 requires, created and re-checked on every
 * availability pass. Order matters only in that a parent precedes its child, so
 * a substrate that does not create intermediate directories still ends up with
 * the same tree.
 */
export const JOURNAL_DIRECTORIES: readonly string[] = Object.freeze([
  JOURNAL_ROOT,
  JOURNAL_ENTRIES_DIRECTORY,
  JOURNAL_DRAFTS_DIRECTORY,
  JOURNAL_ATTACHMENTS_DIRECTORY,
  JOURNAL_GOALS_DIRECTORY,
  JOURNAL_STAGING_DIRECTORY,
  JOURNAL_QUARANTINE_DIRECTORY,
  journalPathJoin(JOURNAL_QUARANTINE_DIRECTORY, JOURNAL_ENTRIES_DIRECTORY),
  journalPathJoin(JOURNAL_QUARANTINE_DIRECTORY, JOURNAL_DRAFTS_DIRECTORY),
  journalPathJoin(JOURNAL_QUARANTINE_DIRECTORY, JOURNAL_GOALS_DIRECTORY),
]);

/** The live directory a record type is stored in. */
export function journalLiveDirectory(type: JournalRecordType): string {
  switch (type) {
    case "entry":
      return JOURNAL_ENTRIES_DIRECTORY;
    case "draft":
      return JOURNAL_DRAFTS_DIRECTORY;
    case "goal":
      return JOURNAL_GOALS_DIRECTORY;
  }
}

/** The quarantine directory a preserved record of that type is copied into. */
export function journalQuarantineDirectory(type: JournalRecordType): string {
  return journalPathJoin(JOURNAL_QUARANTINE_DIRECTORY, journalLiveDirectory(type));
}

/* ------------------------------------------------------------------------- *
 * Record files: the filename IS the identity
 * ------------------------------------------------------------------------- */

export const JOURNAL_RECORD_EXTENSION = ".json";
export const JOURNAL_ATTACHMENT_EXTENSION = ".data";

/**
 * `<lowercased id>.json`.
 *
 * Lowercasing is not cosmetic: identity is recovered from the filename
 * (F-JP-18), so two spellings of one uuid would be two records claiming one id,
 * and the second write would not find the first.
 */
export function journalRecordFileName(id: string): string {
  return `${id.toLowerCase()}${JOURNAL_RECORD_EXTENSION}`;
}

/**
 * The identity a record file's name asserts, or `undefined` when the name is
 * not a record file at all.
 *
 * Returning `undefined` rather than a guess is the point: a decoded id that
 * disagrees with this is `invalidIdentity` corruption, and a filename parser
 * that invented an identity would make that comparison always succeed.
 */
export function journalRecordIdFromFileName(name: string): string | undefined {
  if (!name.endsWith(JOURNAL_RECORD_EXTENSION)) return undefined;
  const id = name.slice(0, name.length - JOURNAL_RECORD_EXTENSION.length);
  if (id.length === 0) return undefined;
  // A name that is not already lowercase does not assert the identity this
  // repository writes, so it is not accepted as one.
  return id === id.toLowerCase() ? id : undefined;
}

export function journalRecordPath(type: JournalRecordType, id: string): string {
  return journalPathJoin(journalLiveDirectory(type), journalRecordFileName(id));
}

export function journalQuarantineRecordPath(
  type: JournalRecordType,
  id: string,
): string {
  return journalPathJoin(journalQuarantineDirectory(type), journalRecordFileName(id));
}

/** `Attachments/{owner uuid}` — one directory per owning entry (F-JP-18). */
export function journalAttachmentDirectory(ownerEntryId: string): string {
  return journalPathJoin(JOURNAL_ATTACHMENTS_DIRECTORY, ownerEntryId.toLowerCase());
}

/** `Attachments/{owner uuid}/{attachment uuid}.data`. */
export function journalAttachmentPath(
  ownerEntryId: string,
  attachmentId: string,
): string {
  return journalPathJoin(
    journalAttachmentDirectory(ownerEntryId),
    `${attachmentId.toLowerCase()}${JOURNAL_ATTACHMENT_EXTENSION}`,
  );
}

/* ------------------------------------------------------------------------- *
 * Approved roots
 * ------------------------------------------------------------------------- */

/**
 * The six roots a preserved record may legitimately live under (F-JP-33). A row
 * whose path is outside them is `unsafeRecoveryPath` — this is what stops a
 * stored string from turning an export into an arbitrary file read.
 */
export const JOURNAL_RECOVERY_ROOTS: readonly string[] = Object.freeze([
  JOURNAL_ENTRIES_DIRECTORY,
  JOURNAL_DRAFTS_DIRECTORY,
  JOURNAL_GOALS_DIRECTORY,
  journalPathJoin(JOURNAL_QUARANTINE_DIRECTORY, JOURNAL_ENTRIES_DIRECTORY),
  journalPathJoin(JOURNAL_QUARANTINE_DIRECTORY, JOURNAL_DRAFTS_DIRECTORY),
  journalPathJoin(JOURNAL_QUARANTINE_DIRECTORY, JOURNAL_GOALS_DIRECTORY),
]);

/**
 * The four roots a deletion transaction may move out of (F-JP-21).
 *
 * Quarantine and Staging are deliberately absent. A transaction that could
 * stage a quarantined file would be able to destroy the only intact copy of a
 * record the user has not recovered yet, and one that could stage another
 * bundle's contents would be able to undo a recovery in progress.
 */
export const JOURNAL_DELETION_ROOTS: readonly string[] = Object.freeze([
  JOURNAL_ENTRIES_DIRECTORY,
  JOURNAL_DRAFTS_DIRECTORY,
  JOURNAL_GOALS_DIRECTORY,
  JOURNAL_ATTACHMENTS_DIRECTORY,
]);

function isUnder(path: string, roots: readonly string[]): boolean {
  // Strictly inside: the root itself is never a target. Deleting `Entries`
  // wholesale is not something any transaction in this package asks for, and
  // allowing it would make one malformed manifest able to empty the library.
  return roots.some((root) => path.startsWith(`${root}/`));
}

export function isApprovedJournalRecoveryPath(path: string): boolean {
  if (!isSafeJournalPath(path)) return false;
  return isUnder(path, JOURNAL_RECOVERY_ROOTS);
}

export function isApprovedJournalDeletionPath(path: string): boolean {
  if (!isSafeJournalPath(path)) return false;
  return isUnder(path, JOURNAL_DELETION_ROOTS);
}

/**
 * A path made only of safe segments.
 *
 * `.` and `..` are rejected before any prefix check, because `Entries/../..`
 * starts with an approved root and resolves outside the Journal entirely — the
 * prefix test alone is not a containment test.
 */
export function isSafeJournalPath(path: string): boolean {
  if (path.length === 0) return false;
  if (path.startsWith("/")) return false;
  return path.split("/").every(isSafeJournalName);
}

/**
 * A single path segment a bundle may use: non-empty, not `.` or `..`, and free
 * of separators (F-JP-21).
 */
export function isSafeJournalName(name: string): boolean {
  if (name.length === 0) return false;
  if (name === "." || name === "..") return false;
  return !name.includes("/") && !name.includes("\\");
}

/* ------------------------------------------------------------------------- *
 * Staged names
 * ------------------------------------------------------------------------- */

/**
 * The fixed staged names F-JP-21 lists, plus the `attachment-{uuid}.data` form
 * used when a transaction stages individual attachment files rather than a
 * whole owner directory.
 */
export const JOURNAL_STAGED_ENTRY = "entry.json";
export const JOURNAL_STAGED_DRAFT = "draft.json";
export const JOURNAL_STAGED_GOAL = "goal.json";
export const JOURNAL_STAGED_ATTACHMENTS = "attachments";
export const JOURNAL_STAGED_ATTACHMENT = "attachment.data";

export function journalStagedAttachmentName(attachmentId: string): string {
  return `attachment-${attachmentId.toLowerCase()}${JOURNAL_ATTACHMENT_EXTENSION}`;
}

export function journalStagedRecordName(type: JournalRecordType): string {
  switch (type) {
    case "entry":
      return JOURNAL_STAGED_ENTRY;
    case "draft":
      return JOURNAL_STAGED_DRAFT;
    case "goal":
      return JOURNAL_STAGED_GOAL;
  }
}

/* ------------------------------------------------------------------------- *
 * Staging: delete bundles, write backups, write-recovery files
 * ------------------------------------------------------------------------- */

export const JOURNAL_DELETE_BUNDLE_PREFIX = "delete-";
export const JOURNAL_DELETE_MANIFEST_NAME = "manifest.json";
export const JOURNAL_DELETE_COMMITTED_NAME = "committed";
export const JOURNAL_WRITE_BACKUP_PREFIX = "backup-";
export const JOURNAL_WRITE_RECOVERY_PREFIX = "write-recovery-";
/** Only this prefix is swept by staging cleanup (F-JP-29). */
export const JOURNAL_IMPORT_STAGING_PREFIX = "import-";

export function journalDeleteBundlePath(bundleId: string): string {
  return journalPathJoin(
    JOURNAL_STAGING_DIRECTORY,
    `${JOURNAL_DELETE_BUNDLE_PREFIX}${bundleId}`,
  );
}

export function isJournalDeleteBundleName(name: string): boolean {
  return name.startsWith(JOURNAL_DELETE_BUNDLE_PREFIX) && isSafeJournalName(name);
}

/**
 * Where the previous bytes of a destination are parked while it is rewritten.
 *
 * The name is derived from the destination so two concurrent writes to
 * different records cannot share a backup, and a crash leaves a file whose name
 * says which record it belongs to.
 */
export function journalWriteBackupPath(destination: string, token: string): string {
  return journalPathJoin(
    JOURNAL_STAGING_DIRECTORY,
    `${JOURNAL_WRITE_BACKUP_PREFIX}${token}-${flattenPath(destination)}`,
  );
}

/**
 * Where a backup is preserved when a rollback could not be verified (F-JP-20).
 *
 * It is a *rename*, never a delete: at that moment the backup may be the only
 * intact copy of the record, and cleaning it up would turn a recoverable
 * failure into a permanent loss.
 */
export function journalWriteRecoveryPath(destination: string, token: string): string {
  return journalPathJoin(
    JOURNAL_STAGING_DIRECTORY,
    `${JOURNAL_WRITE_RECOVERY_PREFIX}${token}-${flattenPath(destination)}`,
  );
}

/** Separators become `_` so a derived staging name stays a single segment. */
function flattenPath(path: string): string {
  return path.replace(/\//g, "_");
}
